import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';

// --- Color Palette ---
const COLORS = {
    rawMaterial: { bg: '#E3F2FD', border: '#1976D2', text: '#0D47A1', accent: '#1976D2' },
    product: { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20', accent: '#2E7D32' },
    dispatch: { bg: '#FFF3E0', border: '#E65100', text: '#BF360C', accent: '#EF6C00' },
    waste: { bg: '#FFEBEE', border: '#C62828', text: '#B71C1C', accent: '#E53935' },
    tested: { bg: '#E8EAF6', border: '#283593', text: '#1A237E', accent: '#3949AB' },
    usedProduct: { bg: '#F3E5F5', border: '#6A1B9A', text: '#4A148C', accent: '#8E24AA' },
    line: '#B0BEC5',
    lineActive: '#78909C',
    bg: '#fafbfc',
};

// --- Icons (inline SVG) ---
const RawIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="7" width="20" height="10" rx="2" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" />
        <path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" />
        <line x1="12" y1="10" x2="12" y2="14" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="10" y1="12" x2="14" y2="12" stroke={COLORS.rawMaterial.accent} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
const ProductIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2 17l10 5 10-5" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2 12l10 5 10-5" stroke={COLORS.product.accent} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);
const DispatchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke={COLORS.dispatch.accent} strokeWidth="1.5" />
        <path d="M3 10h18" stroke={COLORS.dispatch.accent} strokeWidth="1.5" />
        <path d="M10 15l2 2 4-4" stroke={COLORS.dispatch.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const WasteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H8a2 2 0 01-2-2V6h12z" stroke={COLORS.waste.accent} strokeWidth="1.5" />
    </svg>
);
const TestedIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M9 3h6v6l3 10H6L9 9V3z" stroke={COLORS.tested.accent} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 3h6" stroke={COLORS.tested.accent} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
const UsedProdIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke={COLORS.usedProduct.accent} strokeWidth="1.5" />
        <path d="M12 8v8M8 12h8" stroke={COLORS.usedProduct.accent} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
const GroupIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2c6ecb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
        <polyline points="15 3 21 3 21 9"></polyline>
        <line x1="10" y1="14" x2="21" y2="3"></line>
    </svg>
);

// Helpers
const pct = (part, total) => {
    if (total <= 0) return 0;
    const p = (part / total) * 100;
    if (p > 0 && p < 1) return p.toFixed(2);
    return Math.round(p * 100) / 100;
};

// Node Component
const FlowNode = ({ x, y, w, h, colors, icon, title, subtitle, badge, uuid, type, currentUuid, currentLabel, onShowSimple, onShowGroup }) => {
    const navigate = useNavigate();
    const { workspaceUuid } = useParams();
    const isNavigable = (!!uuid && uuid !== currentUuid) || (currentLabel === 'Current' && !!onShowSimple);

    const handleNav = (e) => {
        if (type === 'groupLink' && onShowGroup) {
            e.stopPropagation();
            onShowGroup();
            return;
        }
        if (currentLabel === 'Current' && onShowSimple) {
            e.stopPropagation();
            onShowSimple(uuid);
            return;
        }
        if (!isNavigable || !!currentLabel) return; // Don't use handleNav for current node (it has its own label click, but now we'll allow node click too)

        e.stopPropagation();
        const base = type === 'raw' ? `/workspace/${workspaceUuid}/raw-materials` : `/workspace/${workspaceUuid}/products`;
        navigate(`${base}/${uuid}?autoOpenOverview=true`);
    };

    const handleLabelClick = (e) => {
        if (currentLabel === 'Current' && onShowSimple) {
            e.stopPropagation();
            onShowSimple(uuid);
        }
    };

    return (
        <g style={{ cursor: (isNavigable || type === 'groupLink') ? 'pointer' : 'default' }} onClick={handleNav}>
            <rect
                x={x} y={y} width={w} height={h} rx={12}
                fill={colors.bg} stroke={colors.border} strokeWidth="1.5"
                style={{ filter: 'drop-shadow(0px 2px 6px rgba(0,0,0,0.08))' }}
            />
            {isNavigable && (
                <g transform={`translate(${x + w - 24}, ${y + 8})`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2c6ecb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                </g>
            )}
            <foreignObject x={x} y={y} width={w} height={h}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: '100%', width: '100%', padding: '4px 8px', boxSizing: 'border-box', textAlign: 'center',
                    userSelect: 'none'
                }}>
                    <div style={{ marginBottom: 2 }}>{icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: colors.text, lineHeight: 1.1, maxWidth: w - 12 }}>{title}</div>
                    {subtitle && <div style={{ fontSize: 9, color: colors.text, opacity: 0.7, marginTop: 1 }}>{subtitle}</div>}
                    {badge && (
                        <div style={{
                            marginTop: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: colors.accent,
                            borderRadius: 99, padding: '1px 8px', lineHeight: '16px'
                        }}>{badge}</div>
                    )}
                </div>
            </foreignObject>
            {currentLabel && (
                <g onClick={handleLabelClick} style={{ cursor: onShowSimple ? 'pointer' : 'default' }}>
                    <text
                        x={x + w / 2}
                        y={y + h + 15}
                        textAnchor="middle"
                        fill={colors.accent}
                        style={{ fontSize: 10, fontWeight: 700 }}
                    >
                        {currentLabel}
                    </text>
                </g>
            )}
        </g>
    );
};

// --- Connection (curved path) ---
const FlowLine = ({ x1, y1, x2, y2, label, labelColor, forceIsRtl }) => {
    const { isRtl: contextIsRtl } = useLanguage();
    const isRtl = forceIsRtl !== undefined ? forceIsRtl : contextIsRtl;
    const midY = (y1 + y2) / 2;
    const d = `M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`;
    const labelX = (x1 + x2) / 2;
    const labelY = midY;
    return (
        <g>
            <path d={d} fill="none" stroke={COLORS.line} strokeWidth="2" strokeDasharray="6,3" style={{ opacity: 0.6 }} />
            <path d={d} fill="none" stroke={COLORS.lineActive} strokeWidth="2" style={{ opacity: 0 }}>
                <animate attributeName="opacity" values="0;0.5;0" dur="3s" repeatCount="indefinite" />
            </path>
            {/* arrowhead on target */}
            <circle cx={x2} cy={y2} r="4" fill={COLORS.lineActive} style={{ opacity: 0.6 }} />
            {label && (
                <g>
                    <rect x={labelX - 22} y={labelY - 9} width={44} height={18} rx={9} fill={labelColor || '#455A64'} style={{ opacity: 0.9 }} />
                    <text x={labelX} y={labelY + 4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">{typeof label === 'number' ? `${label.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 1 })}%` : label}</text>
                </g>
            )}
        </g>
    );
};

// --- Main Diagram ---
const ProductFlowDiagram = ({ product, wasteProducts, dispatches, downstreamProducts, groupProducts, currentUuid, onShowGroup, onShowSimple, forceT, forceIsRtl }) => {
    const { t: contextT, isRtl: contextIsRtl } = useLanguage();
    const t = forceT || contextT;
    const isRtl = forceIsRtl !== undefined ? forceIsRtl : contextIsRtl;
    const rawMats = product?.used_raw_materials || [];
    const usedProds = product?.used_products || [];
    const wastes = wasteProducts || [];
    const disps = dispatches || [];

    const totalRawQty = rawMats.reduce((s, u) => s + Number(u.quantity || 0), 0);
    const totalUsedProdQty = usedProds.reduce((s, u) => s + Number(u.quantity || 0), 0);
    const productQty = Number(product?.product_quantiry || 0);
    const totalInputQty = totalRawQty + totalUsedProdQty;

    const downstream = downstreamProducts || [];
    const totalDownstreamQty = downstream.reduce((s, dp) => {
        const consumed = dp.used_products.find(up => up.productId?._id === product._id || up.productId === product._id);
        return s + Number(consumed?.quantity || 0);
    }, 0);

    const isGroupBatch = groupProducts && groupProducts.length > 0;

    const wasteList = wastes.filter(w => w.type === 'waste');
    const testedList = wastes.filter(w => w.type === 'tested');

    const totalWasteQty = isGroupBatch && wasteList.length > 0
        ? Number(wasteList[0].quantity || 0)
        : wasteList.reduce((s, w) => s + Number(w.quantity || 0), 0);

    const totalTestedQty = isGroupBatch && testedList.length > 0
        ? Number(testedList[0].quantity || 0)
        : testedList.reduce((s, w) => s + Number(w.quantity || 0), 0);

    const totalDispatchQty = disps.reduce((s, d) => {
        const pInD = (d.products || []).find(p => p.productId?._id === product._id || p.productId === product._id);
        return s + Number(pInD?.quantity || 0);
    }, 0);

    const NODE_W = 140;
    const NODE_H = 72;
    const PRODUCT_W = 180;
    const PRODUCT_H = 80;
    const SMALL_NODE_W = 130;
    const SMALL_NODE_H = 64;
    const GAP_X = 20;
    const Y_RAW = 30;
    const Y_PRODUCT = 200;
    const Y_OUT = 380;

    const isGroupMember = !!product?.groupId;
    const hideInputs = isGroupMember && !isGroupBatch;

    const inputCount = hideInputs ? 1 : (rawMats.length + usedProds.length || 1);
    const topRowWidth = inputCount * NODE_W + (inputCount - 1) * GAP_X;

    const outItems = [];

    if (groupProducts && groupProducts.length > 0) {
        groupProducts.forEach(p => {
            outItems.push({
                type: 'product',
                label: p.product_name,
                qty: Number(p.quantityProduced || p.product_quantiry || p.product_quantity || 0),
                icon: <ProductIcon />,
                colors: COLORS.product,
                uuid: p.uuid
            });
        });
    }

    if (totalDispatchQty > 0) outItems.push({ type: 'dispatch', label: t.dispatched, qty: totalDispatchQty, icon: <DispatchIcon />, colors: COLORS.dispatch });
    if (totalWasteQty > 0 && !(isGroupMember && !isGroupBatch)) {
        outItems.push({ type: 'waste', label: t.waste, qty: totalWasteQty, icon: <WasteIcon />, colors: COLORS.waste });
    }
    if (totalTestedQty > 0) outItems.push({ type: 'tested', label: t.tested, qty: totalTestedQty, icon: <TestedIcon />, colors: COLORS.tested });

    downstream.forEach(dp => {
        const consumed = dp.used_products.find(up => up.productId?._id === product._id || up.productId === product._id);
        outItems.push({
            type: 'usedProduct',
            label: dp.product_name,
            qty: Number(consumed?.quantity || 0),
            icon: <UsedProdIcon />,
            colors: COLORS.usedProduct,
            uuid: dp.uuid
        });
    });

    if (!isGroupBatch && productQty > 0) {
        outItems.push({ type: 'stock', label: t.in_stock, qty: productQty, icon: <ProductIcon />, colors: COLORS.product });
    }

    const outCount = outItems.length;
    const bottomRowWidth = outCount * SMALL_NODE_W + (outCount - 1) * GAP_X;

    const svgWidth = Math.max(topRowWidth, bottomRowWidth, PRODUCT_W) + 120;
    const svgHeight = Y_OUT + SMALL_NODE_H + 40;
    const centerX = svgWidth / 2;

    const inputPositions = hideInputs ? [{
        x: centerX - NODE_W / 2,
        y: Y_RAW,
        type: 'groupLink',
        data: { name: t.group_production }
    }] : [
        ...rawMats.map((_, i) => ({
            x: centerX - topRowWidth / 2 + i * (NODE_W + GAP_X),
            y: Y_RAW,
            type: 'raw',
            data: rawMats[i]
        })),
        ...usedProds.map((_, i) => ({
            x: centerX - topRowWidth / 2 + (rawMats.length + i) * (NODE_W + GAP_X),
            y: Y_RAW,
            type: 'prod',
            data: usedProds[i]
        }))
    ];

    const productPos = { x: centerX - PRODUCT_W / 2, y: Y_PRODUCT };

    const outPositions = outItems.map((_, i) => ({
        x: centerX - bottomRowWidth / 2 + i * (SMALL_NODE_W + GAP_X),
        y: Y_OUT,
    }));

    const groupProdsQty = isGroupBatch
        ? (groupProducts || []).reduce((s, p) => s + Number(p.product_quantiry || p.product_quantity || 0), 0)
        : productQty;

    const totalOutputQty = totalDispatchQty + totalWasteQty + totalTestedQty + totalDownstreamQty + groupProdsQty;
    const finalDenom = product?.quantityProduced || totalOutputQty || productQty;

    return (
        <div style={{ overflow: 'visible', padding: 20, background: COLORS.bg, borderRadius: 12, minHeight: svgHeight + 80 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16, justifyContent: 'center', direction: isRtl ? 'rtl' : 'ltr' }}>
                {[
                    { label: t.raw_material, color: COLORS.rawMaterial.accent },
                    { label: t.product, color: COLORS.product.accent },
                    { label: t.dispatched, color: COLORS.dispatch.accent },
                    { label: t.waste, color: COLORS.waste.accent },
                    { label: t.tested, color: COLORS.tested.accent },
                    { label: t.used_in_product, color: COLORS.usedProduct.accent },
                ].map((l, i) => (
                    <div key={`legend-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: l.color }} />
                        <span style={{ fontSize: 11, color: '#455A64', fontWeight: 500 }}>{l.label}</span>
                    </div>
                ))}
            </div>

            <svg width={svgWidth} height={svgHeight} style={{ display: 'block', margin: '0 auto' }}>
                {inputPositions.map((pos, i) => {
                    const fromX = pos.x + NODE_W / 2;
                    const fromY = pos.y + NODE_H;
                    const toX = productPos.x + PRODUCT_W / 2;
                    const toY = productPos.y;
                    const qty = Number(pos.data.quantity || 0);
                    const percentage = pct(qty, totalInputQty);
                    return (
                        <FlowLine
                            key={`input-${i}`}
                            x1={fromX} y1={fromY}
                            x2={toX} y2={toY}
                            label={pos.type === 'groupLink' ? null : `${percentage.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 1 })}%`}
                            labelColor={pos.type === 'raw' ? COLORS.rawMaterial.accent : COLORS.usedProduct.accent}
                            forceIsRtl={forceIsRtl}
                        />
                    );
                })}

                {outItems.map((item, i) => {
                    const pos = outPositions[i];
                    const fromX = productPos.x + PRODUCT_W / 2;
                    const fromY = productPos.y + PRODUCT_H;
                    const toX = pos.x + SMALL_NODE_W / 2;
                    const toY = pos.y;
                    const percentage = finalDenom > 0 ? pct(item.qty, finalDenom) : 100;
                    return (
                        <FlowLine
                            key={`out-${i}`}
                            x1={fromX} y1={fromY}
                            x2={toX} y2={toY}
                            label={`${percentage.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 1 })}%`}
                            labelColor={item.colors.accent}
                            forceIsRtl={forceIsRtl}
                        />
                    );
                })}

                {inputPositions.length > 0 ? inputPositions.map((pos, i) => {
                    if (pos.type === 'groupLink') {
                        return (
                            <FlowNode
                                key={`group-link-${i}`}
                                x={pos.x} y={pos.y} w={NODE_W} h={NODE_H}
                                colors={{ bg: '#F5F3FF', border: '#7C3AED', text: '#5B21B6', accent: '#7C3AED' }}
                                icon={<GroupIcon />}
                                title={t.group_production}
                                subtitle={t.view_shared_batch}
                                type="groupLink"
                                onShowGroup={onShowGroup}
                            />
                        );
                    }
                    if (pos.type === 'raw') {
                        const name = pos.data.rawMaterialId?.material_name || pos.data.rawMaterialName || t.material;
                        return (
                            <FlowNode
                                key={`input-${i}`}
                                x={pos.x} y={pos.y} w={NODE_W} h={NODE_H}
                                colors={COLORS.rawMaterial}
                                icon={<RawIcon />}
                                title={name}
                                subtitle={`${Number(pos.data.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} ${t.kg}`}
                                uuid={pos.data.rawMaterialId?.uuid || pos.data.uuid}
                                type="raw"
                                currentUuid={currentUuid}
                                onShowSimple={onShowSimple}
                                onShowGroup={onShowGroup}
                            />
                        );
                    } else {
                        const name = pos.data.productId?.product_name || t.product;
                        return (
                            <FlowNode
                                key={`input-${i}`}
                                x={pos.x} y={pos.y} w={NODE_W} h={NODE_H}
                                colors={COLORS.usedProduct}
                                icon={<UsedProdIcon />}
                                title={name}
                                subtitle={`${Number(pos.data.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} ${t.kg}`}
                                uuid={pos.data.productId?.uuid || pos.data.uuid}
                                type="product"
                                currentUuid={currentUuid}
                                onShowSimple={onShowSimple}
                                onShowGroup={onShowGroup}
                            />
                        );
                    }
                }) : (
                    <FlowNode
                        key="no-inputs"
                        x={centerX - NODE_W / 2} y={Y_RAW} w={NODE_W} h={NODE_H}
                        colors={COLORS.rawMaterial}
                        icon={<RawIcon />}
                        title={t.no_inputs}
                        subtitle=""
                    />
                )}

                <FlowNode
                    x={productPos.x} y={productPos.y} w={PRODUCT_W} h={PRODUCT_H}
                    colors={COLORS.product}
                    icon={<ProductIcon />}
                    title={`${product?.product_name || t.product} ${product?.batch ? `(${t.batch}: ${product.batch})` : ''}`}
                    subtitle={`${Number(product?.quantityProduced || finalDenom).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} ${t.kg}`}
                    badge={product?.product_name}
                    uuid={product?.uuid}
                    type="product"
                    currentUuid={currentUuid}
                    onShowSimple={onShowSimple}
                    onShowGroup={onShowGroup}
                />

                {outItems.map((item, i) => {
                    const pos = outPositions[i];
                    return (
                        <FlowNode
                            key={`out-${i}`}
                            x={pos.x} y={pos.y} w={SMALL_NODE_W} h={SMALL_NODE_H}
                            colors={item.colors}
                            icon={item.icon}
                            title={item.label}
                            subtitle={`${Number(item.qty || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} ${t.kg}`}
                            currentLabel={isGroupBatch && item.uuid === currentUuid ? t.current_label : null}
                            uuid={item.type === 'product' || item.type === 'usedProduct' ? item.uuid : null}
                            type="product"
                            currentUuid={currentUuid}
                            onShowSimple={onShowSimple}
                            onShowGroup={onShowGroup}
                        />
                    );
                })}
            </svg>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 24, direction: isRtl ? 'rtl' : 'ltr' }}>
                <SummaryCard label={t.total_input} value={`${totalInputQty.toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color={COLORS.rawMaterial.accent} />
                <SummaryCard label={t.produced} value={`${(product?.quantityProduced || finalDenom).toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color={COLORS.product.accent} />
                {totalDispatchQty > 0 && <SummaryCard label={t.dispatched} value={`${totalDispatchQty} (${pct(totalDispatchQty, finalDenom)}%)`} color={COLORS.dispatch.accent} />}
                {totalWasteQty > 0 && !(isGroupMember && !isGroupBatch) && <SummaryCard label={t.waste} value={`${totalWasteQty} (${pct(totalWasteQty, finalDenom)}%)`} color={COLORS.waste.accent} />}
                {totalTestedQty > 0 && <SummaryCard label={t.tested} value={`${totalTestedQty} (${pct(totalTestedQty, finalDenom)}%)`} color={COLORS.tested.accent} />}
                {totalDownstreamQty > 0 && <SummaryCard label={t.consumed_in_mfg} value={`${totalDownstreamQty} (${pct(totalDownstreamQty, finalDenom)}%)`} color={COLORS.usedProduct.accent} />}
                <SummaryCard label={t.in_stock} value={`${productQty.toLocaleString(isRtl ? 'ar-EG' : undefined)} ${t.kg}`} color="#1976d2" />
            </div>
        </div>
    );
};

const SummaryCard = ({ label, value, color }) => (
    <div style={{
        background: '#fff', borderRadius: 10, padding: '12px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #ebebeb',
        minWidth: 120, textAlign: 'center'
    }}>
        <div style={{ fontSize: 11, color: '#6d7175', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
);

export default ProductFlowDiagram;
